/**
 * What the chat panel shows while it waits on a turn this tab is not
 * streaming (`turnStillWorking` on `useAgentChat`): someone else's turn, this
 * tab's own stopped turn still holding the session, or a wait given up.
 * Split out of `BirdChatPanel.tsx` to keep it under the file-size limit.
 */
import React from 'react';
import { Box, VisuallyHidden } from '@chakra-ui/react';

import type { ChatMessage, TurnStillWorking } from '../../../hooks/data/useAgentChat';
import { isRefusalNotice } from '../../../hooks/data/agentChatReducer';
import { StatusBanner } from '../../ui/StatusBanner';
import { BirdAgentActivity } from './BirdAgentActivity';

/**
 * The activity line while this tab waits, keyed by the state. A state with no
 * entry shows none, and does not lock the composer.
 */
const WAITING_LINE: Partial<Record<TurnStillWorking, string>> = {
  working: 'Thinking',
  stopping: 'Stopping',
};

/** Whether the composer is locked behind a turn this tab is not streaming. */
export function waitsOnTurn(turnStillWorking: TurnStillWorking): boolean {
  return WAITING_LINE[turnStillWorking] !== undefined;
}

/**
 * The composer's primary action for a turn: Stop while one runs, whether this
 * tab streams it or not. A turn running on another request (another tab or
 * device, a conversation come back to mid-reply, a refused send) is still the
 * caller's own, and Stop ends it on the server, keyed on user and session
 * (`stopAgentTurn`). Only this tab's own stop, still unwinding, locks with
 * nothing to press.
 */
export function composerTurnState(
  isOwnTurnStreaming: boolean,
  turnStillWorking: TurnStillWorking,
): { readonly isStreaming: boolean; readonly isWaiting: boolean } {
  return {
    isStreaming: isOwnTurnStreaming || turnStillWorking === 'working',
    isWaiting: turnStillWorking === 'stopping',
  };
}

/**
 * Nothing of another request's progress reaches this tab, so the plain
 * activity line until the transcript re-read brings the answer; or "Stopping"
 * for this tab's own stopped turn.
 */
export function WaitingActivityLine({
  turnStillWorking,
}: {
  readonly turnStillWorking: TurnStillWorking;
}): React.ReactElement | null {
  const label = WAITING_LINE[turnStillWorking];
  if (label === undefined) return null;
  return <BirdAgentActivity activities={[]} currentAgent={null} idleLabel={label} />;
}

/**
 * Says a refused (or still-stopping) send out loud. The notice row mounts
 * already holding its text, which screen readers commonly do not announce, and
 * the researcher's own message has just left the transcript. So this region is
 * mounted from the start and only its text changes, to the newest refusal
 * notice, which is what a polite live region announces.
 */
export function RefusalAnnouncer({
  messages,
}: {
  readonly messages: ReadonlyArray<ChatMessage>;
}): React.ReactElement {
  const notice = [...messages].reverse().find(isRefusalNotice);
  return (
    <VisuallyHidden role="status" aria-live="polite" data-testid="refusal-announcer">
      {/* Keyed by the notice, so a repeat refusal is a new node and is announced. */}
      <span key={notice?.id ?? 'none'}>{notice?.content ?? ''}</span>
    </VisuallyHidden>
  );
}

/**
 * Past the wait cap for a turn running on another request: it may still be
 * running, but this tab has stopped re-reading, so it says how to check rather
 * than spinning forever. Its own component so the panel's branch count stays
 * where it was.
 */
export function StoppedWaitingBanner({
  turnStillWorking,
  inset = true,
}: {
  readonly turnStillWorking: TurnStillWorking;
  /** The chat panel's own padding; a surface that lays out its own passes false. */
  readonly inset?: boolean;
}): React.ReactElement | null {
  if (turnStillWorking !== 'gave_up') return null;
  const banner = (
    <StatusBanner
      tone="info"
      title="Still working on your last message"
      description="Refresh to check again."
    />
  );
  return inset ? (
    <Box px={4} pt={3}>
      {banner}
    </Box>
  ) : (
    banner
  );
}
